import { useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { isEmpty, noop } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Components
import ValidatableDropdown, { type ValidatableDropdownProps } from 'components/common/ValidatableDropdown';
// Import Constants
import { l } from 'constants/common';

interface Props extends ValidatableDropdownProps {
    data?: { label: string; value: string | number }[];
    value?: (string | number)[];
    onChange?: (selectedValues: (string | number)[], selectedOptions: AnyObject[]) => void;
    onClose?: () => void;
    placeholder?: string;
    disabled?: boolean;
    isShowLabelWithId?: boolean;
}

export interface MultiselectDropdownFormRef {
    submitForm: () => Promise<boolean>;
    reset: () => void;
}

const MultiselectDropdownForm = forwardRef<MultiselectDropdownFormRef, Props>(
    ({ disabled, data = [], value = [], onChange, onClose, placeholder, isShowLabelWithId, ...rest }, ref) => {
        const { t } = useTranslation();

        const formik = useFormik({
            initialValues: {
                selected: [],
            },
            onSubmit: noop,
            validationSchema: Yup.object({
                selected: Yup.array()
                    .required(t(l.CannotBeEmpty, { fieldName: placeholder || l.Value }))
                    .min(1, t(l.CannotBeEmpty, { fieldName: placeholder || l.Value }))
                    .nullable(true),
            }),
        });

        const { values, setFieldValue, errors, touched, submitForm, validateForm, setFieldTouched } = formik;
        const { selected } = values;

        useImperativeHandle(ref, () => ({
            submitForm() {
                return new Promise((resolve, reject) => {
                    submitForm()
                        .then(() =>
                            validateForm(values).then((formErrors) => {
                                resolve(isEmpty(formErrors));
                                return;
                            }),
                        )
                        .catch(() => reject());
                });
            },
            reset() {
                setFieldTouched('selected', false);
            },
        }));

        const changeHandler = (e) => {
            const tmpSelected = e.map((item) => item.value);
            onChange?.(tmpSelected, e);
        };

        useEffect(() => {
            setFieldValue('selected', value);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [value]);

        const dropdownData = useMemo(() => {
            if (isShowLabelWithId) {
                return data.map((item) => ({
                    label: `${item.label} (${item.value})`,
                    value: item.value,
                }));
            }
            return data;
        }, [isShowLabelWithId, data]);

        return (
            <ValidatableDropdown
                isMultiSelect={true}
                disabled={disabled}
                hasSearch={true}
                inputSize="default"
                labelAppearance="swap"
                value={selected}
                data={dropdownData}
                onChange={changeHandler}
                onClose={onClose}
                searchPlaceholderText={t(l.Search)}
                noDataText={t(l.NoDataFound)}
                placeholder={placeholder}
                isValid={!(touched.selected && errors.selected)}
                errorText={touched.selected && errors.selected}
                {...rest}
            />
        );
    },
);

MultiselectDropdownForm.displayName = 'MultiselectDropdownForm';

export default MultiselectDropdownForm;
